import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Difficulty } from '../../types';

interface CompletionModalProps {
  isOpen: boolean;
  difficulty: Difficulty;
  levelNumber: number;
  timeSpent: number;
  hintsUsed: number;
  mistakes: number;
  score?: number;
  nextPuzzleId?: number;
  onPlayAgain: () => void;
}

export default function CompletionModal({
  isOpen,
  difficulty,
  levelNumber,
  timeSpent,
  hintsUsed,
  mistakes,
  score,
  nextPuzzleId,
  onPlayAgain,
}: CompletionModalProps) {
  const navigate = useNavigate();

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}分${secs}秒`;
  };

  const handleNextLevel = () => {
    if (nextPuzzleId) {
      navigate(`/game/${nextPuzzleId}`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="glass-card p-5 sm:p-8 max-w-md w-full text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="text-5xl sm:text-6xl mb-3 sm:mb-4"
            >
              🎉
            </motion.div>

            <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">恭喜完成！</h2>
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
              你成功完成了{difficulty === 'beginner' ? '初学者' : difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'}
              难度的第 {levelNumber} 关
            </p>

            <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6 text-left">
              <div className="glass-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">用时</p>
                <p className="text-lg sm:text-xl font-semibold">{formatTime(timeSpent)}</p>
              </div>
              <div className="glass-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">使用提示</p>
                <p className="text-lg sm:text-xl font-semibold">{hintsUsed} 次</p>
              </div>
              <div className="glass-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">错误次数</p>
                <p className="text-lg sm:text-xl font-semibold">{mistakes} 次</p>
              </div>
              {score !== undefined && (
                <div className="glass-card p-3 sm:p-4 bg-gradient-to-br from-yellow-100 to-orange-100">
                  <p className="text-xs sm:text-sm text-gray-500">获得分数</p>
                  <p className="text-lg sm:text-xl font-semibold text-yellow-600">{score} 分</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:gap-3">
              {/* Next Level Button - Primary action */}
              {nextPuzzleId && (
                <button
                  onClick={handleNextLevel}
                  className="w-full btn-primary text-sm sm:text-base touch-manipulation flex items-center justify-center gap-2"
                >
                  下一关
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              
              {/* Secondary actions */}
              <div className="flex gap-2 sm:gap-3">
                <button onClick={onPlayAgain} className="flex-1 btn-secondary text-sm sm:text-base touch-manipulation">
                  再玩一次
                </button>
                <Link to={`/levels/${difficulty}`} className="flex-1 btn-secondary text-center text-sm sm:text-base touch-manipulation">
                  返回关卡
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
